import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Icon } from "../../../components/Component";
import FormSelect from "../../form-components/SelectComponent";
import FormTimePicker from "../../form-components/FormTimePickerProps";
import FormCheckbox from "../../form-components/CheckboxComponent";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import FormInput from "../../form-components/InputComponent";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { timeLogSettingsApis } from "../../../api/TimeLogSettings";
import {
  secondsToTimeString,
  timeStringToSeconds,
} from "../../../utils/convertions";
import { Spin } from "antd/lib";
import { LoadingOutlined } from "@ant-design/icons";
import { Alert, Card } from "reactstrap";
import Swal from "sweetalert2";
import { useLocation, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import FormRadio from "../../form-components/RadioComponent";

const ProjectTimeLog: FC<{}> = () => {
  const location = useLocation();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const { id: stateProjectId, projectName } = location.state || {};
  const projectId = urlProjectId || stateProjectId;
  const dispatch = useDispatch();
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [error, setError] = useState({ status: false, error: "", message: "" });
  const [loading, setLoading] = useState(true);

  const [timeLogSettingsId, setTimeLogSettingsId] = useState<number>(0);
  const [siteIdState, setSiteIdState] = useState<number>(0);
  const [projectIdState, setProjectIdState] = useState<number>(0);

  type RestrictedTimeLogs = "Weekends" | "Holidays" | "Leaves";

  interface ProjectTimeLogProps {
    userRestriction: string;
    allowClosedTaskLog: string;
    allowMultipleTimers: string;
    restrictWorkHours: boolean;
    restrictedTimeLogs: any;
    logConfig: string;
    allowPastTimeLog: boolean;
    allowFutureTimeLog: boolean;
    allowPastTimeLogForm?: string;
    allowFutureTimeLogForm?: string;
    enableHourLimit: boolean;
    dailyHourLimitInSec?: string | number;
    weeklyHourLimitInSec?: string | number;
    enableReminder: boolean;
    notifyTimers?: boolean;
    minLogHourInSec?: string | number;
    dailyReminderInSec?: string | number;
    weeklyReminderInSec?: string | number;
    siteId?: number; // Add this
    projectId?: number; // Add this
  }

  const methods = useForm<ProjectTimeLogProps>({
    mode: "onChange",
    defaultValues: {},
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = methods;

  const enableHourLimitWatch = watch("enableHourLimit");
  const enableReminderWatch = watch("enableReminder");

  const saveChanges = (data: any) => {
    // console.log("Form data:", data);
    // console.log("Dirty fields:", dirtyFields);

    let dirtyData = getDirtyFieldsOnly(data, dirtyFields);
    // console.log("Dirty data:", dirtyData);

    if (!dirtyData || Object.keys(dirtyData).length === 0) {
      // console.log("No changes detected, returning to view mode");
      return setMode("view");
    }

    // Add siteId and projectId from state to the payload
    dirtyData.siteId = siteIdState;
    dirtyData.projectId = projectIdState;

    const {
      restrictedTimeLogs,
      dailyReminderInSec,
      dailyHourLimitInSec,
      minLogHourInSec,
      weeklyHourLimitInSec,
      weeklyReminderInSec,
      allowPastTimeLogForm,
      allowFutureTimeLogForm,
      allowClosedTaskLog,
      allowMultipleTimers,
    } = dirtyData;

    // Fix for Allow Past Time - convert string to boolean
    if (allowPastTimeLogForm !== undefined) {
      dirtyData.allowPastTimeLog = allowPastTimeLogForm === "Allow";
    }

    // Fix for Allow Future Time - convert string to boolean
    if (allowFutureTimeLogForm !== undefined) {
      dirtyData.allowFutureTimeLog = allowFutureTimeLogForm === "Allow";
    }

    // Fix for Allow Closed Task Log - convert string to boolean
    if (allowClosedTaskLog !== undefined) {
      dirtyData.allowClosedTaskLog = allowClosedTaskLog === "true";
    }

    // Fix for Allow Multiple Timers - convert string to boolean
    if (allowMultipleTimers !== undefined) {
      dirtyData.allowMultipleTimers = allowMultipleTimers === "true";
    }

    if (restrictedTimeLogs) {
      dirtyData.restrictWeekend = restrictedTimeLogs.includes("Weekends");
      dirtyData.restrictHoliday = restrictedTimeLogs.includes("Holidays");
      dirtyData.restrictLeave = restrictedTimeLogs.includes("Leaves");
    }
    if (minLogHourInSec)
      dirtyData.minLogHourInSec = timeStringToSeconds(minLogHourInSec);
    if (dailyHourLimitInSec)
      dirtyData.dailyHourLimitInSec = timeStringToSeconds(dailyHourLimitInSec);
    if (dailyReminderInSec)
      dirtyData.dailyReminderInSec = Number(dailyReminderInSec) * 60;
    if (weeklyHourLimitInSec)
      dirtyData.weeklyHourLimitInSec = Number(weeklyHourLimitInSec) * 3600;
    if (weeklyReminderInSec)
      dirtyData.weeklyReminderInSec = Number(weeklyReminderInSec) * 60;

    // console.log("Final data to send to API:", dirtyData);

    updateTimeLogSettings(dirtyData);
  };

  const onSubmit = methods.handleSubmit(saveChanges, (errors) => {
    console.log("Validation errors:", errors);
  });

  const updateTimeLogSettings = async (data: any) => {
    try {
      setLoading(true);
      const response: ApiResponse =
        await timeLogSettingsApis.updateTimeLogSettings(
          timeLogSettingsId,
          data
        );
      if (response.status) {
        Swal.fire({
          title: "Success",
          text: response.message,
          icon: "success",
          confirmButtonText: "OK",
        });
        setMode("view");
        fetchData();
      } else {
        setError({
          status: true,
          error: response.error,
          message: response.message,
        });
        setTimeout(
          () => setError({ status: false, message: "", error: "" }),
          10000
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const response: ApiResponse =
        await timeLogSettingsApis.getTimeLogSettingsListOneByProjectId(
          projectId
        );
      if (response.status) {
        const d = response.data;
        // console.log(d);

        // Set siteId and projectId from API response
        setTimeLogSettingsId(d.id);
        setSiteIdState(d.siteId);
        setProjectIdState(d.projectId);

        reset({
          userRestriction: d.userRestriction,
          allowClosedTaskLog: String(d.allowClosedTaskLog ?? false),
          allowMultipleTimers: String(d.allowMultipleTimers ?? false),
          restrictWorkHours: d.restrictWorkHours || false,
          restrictedTimeLogs: d.restrictHoliday
            ? "Holidays"
            : d.restrictLeave
            ? "Leaves"
            : d.restrictWeekend
            ? "Weekends"
            : null,
          logConfig: d.logConfig,
          allowPastTimeLog: d.allowPastTimeLog || false,
          allowFutureTimeLog: d.allowFutureTimeLog || false,
          allowPastTimeLogForm: d.allowPastTimeLog ? "Allow" : "Don't Allow",
          allowFutureTimeLogForm: d.allowFutureTimeLog
            ? "Allow"
            : "Don't Allow",
          enableHourLimit: d.enableHourLimit || false,
          dailyHourLimitInSec:
            d.dailyHourLimitInSec !== undefined
              ? secondsToTimeString(d.dailyHourLimitInSec)
              : undefined,
          weeklyHourLimitInSec:
            d.weeklyHourLimitInSec !== undefined
              ? (d.weeklyHourLimitInSec / 3600).toFixed(0)
              : undefined,
          minLogHourInSec:
            d.minLogHourInSec !== undefined
              ? secondsToTimeString(d.minLogHourInSec)
              : undefined,
          enableReminder: d.enableReminder || false,
          notifyTimers: d.notifyTimers || false,
          dailyReminderInSec:
            d.dailyReminderInSec !== undefined
              ? (d.dailyReminderInSec / 60).toFixed(0)
              : undefined,
          weeklyReminderInSec:
            d.weeklyReminderInSec !== undefined
              ? (d.weeklyReminderInSec / 60).toFixed(0)
              : undefined,
        });
        // console.log(response, "response");

        // console.log("allowClosedTaskLog", d.allowClosedTaskLog);
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchData();
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [location]);

  if (loading)
    return (
      <Content>
        <Card className="card-bordered">
          <div
            className="card-inner card-inner-lg"
            style={{
              minHeight: "87vh",
              display: "flex", // Add flex display
              justifyContent: "center", // Center horizontally
              alignItems: "center", // Center vertically
            }}
          >
            {/* Remove the Flex component and directly use Spin with a message if needed */}
            <Spin
              indicator={<LoadingOutlined spin />}
              size="large"
              tip="Loading..."
            />
          </div>
        </Card>
      </Content>
    );

  return (
    <>
      <Head title="Time Log Settings" />
      <Content>
        <Card className="p-3">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="fw-bold mb-4">TIME LOG SETTINGS</h4>
            <ul className="link-list-opt no-bdr me-0">
              {mode === "edit" ? (
                <div className="nk-block-tools g-3 me-0">
                  <li className="nk-block-tools-opt">
                    <Button
                      color="primary"
                      size="sm"
                      type="button"
                      onClick={() => {
                        // console.log("Update button clicked");
                        onSubmit();
                      }}
                    >
                      <Icon name="check"></Icon>
                      <span>Update</span>
                    </Button>
                  </li>
                  <li className="nk-block-tools-opt">
                    <span
                      className="btn btn-white btn-outline-light btn-sm cursor-pointer "
                      style={{
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "You won't be able to revert this!",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, cancel it!",
                        }).then((result) => {
                          if (result.isConfirmed) {
                            setMode("view");
                            reset();
                            Swal.fire({
                              title: "Cancelled",
                              icon: "success",
                              text: "Your changes have been cancelled.",
                            });
                            setError({
                              status: false,
                              message: "",
                              error: "",
                            });
                          }
                        });
                      }}
                    >
                      <Icon name="cross" />
                      <span>Cancel</span>
                    </span>
                  </li>
                </div>
              ) : (
                <li className="nk-block-tools-opt">
                  <span
                    className="btn btn-white btn-outline-light btn-sm cursor-pointer "
                    style={{
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setMode("edit");
                    }}
                  >
                    <Icon name="edit" />
                    <span>Edit</span>
                  </span>
                </li>
              )}
            </ul>
          </div>

          <FormProvider {...methods}>
            <form>
              {/* === TOP SECTION BEFORE TIME LOG PERIOD SETTINGS === */}
              <div className="row mb-3">
                <div className="col-md-3 fw-bold">
                  Time Log User Restriction
                </div>
                <div className="col-md-9 d-flex flex-wrap gap-3">
                  {mode === "edit" && errors.userRestriction && (
                    <p className="text-danger small">
                      {errors.userRestriction.message}
                    </p>
                  )}
                  <FormRadio
                    name="userRestriction"
                    value="TaskOwner"
                    optionLabel="Task & Bug Owners"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="userRestriction"
                    value="Anyone"
                    optionLabel="Anyone in Project"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="userRestriction"
                    value="TeamMember"
                    optionLabel="Associated Team Members"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <hr />
              <div className="row mb-3">
                <div className="col-md-3 fw-bold">
                  Log Hours for Closed Tasks and Bugs
                </div>
                <div className="col-md-9 d-flex flex-wrap gap-3">
                  <FormRadio
                    name="allowClosedTaskLog"
                    value="true"
                    optionLabel="Allow"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="allowClosedTaskLog"
                    value="false"
                    optionLabel="Don't Allow"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <hr />

              <div className="row mb-3">
                <div className="col-md-3 fw-bold">
                  Multiple Timers Restriction
                </div>
                <div className="col-md-9 d-flex flex-wrap gap-3">
                  <FormRadio
                    name="allowMultipleTimers"
                    value="true"
                    optionLabel="Allow"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="allowMultipleTimers"
                    value="false"
                    optionLabel="Don't Allow"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <hr />

              <div className="row mb-3">
                <div className="col-md-3 fw-bold">
                  Restriction Based On Task Work Hours
                </div>
                <div className="col-md-9 d-flex align-items-center">
                  <div className="d-flex align-items-center ms-4">
                    <p className="mb-0  text-dark small me-2">
                      Time logs will be limited to the work hours allotted for a
                      task.
                    </p>
                    <FormCheckbox
                      name="restrictWorkHours"
                      optionLabel="Enable"
                      disabled={mode === "view"}
                      required={mode === "edit"}
                    />
                  </div>
                </div>
              </div>
              <hr />

              <div className="row mb-3">
                <div className="col-md-3 fw-bold">Restrict time log(s)</div>
                <div className="col-md-9 d-flex flex-wrap gap-3">
                  <FormRadio
                    name="restrictedTimeLogs"
                    value="Weekends"
                    optionLabel="Weekend"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="restrictedTimeLogs"
                    value="Holidays"
                    optionLabel="Holiday"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="restrictedTimeLogs"
                    value="Leaves"
                    optionLabel="Leave"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <hr />

              <div className="row mb-3">
                <div className="col-md-3 fw-bold">Time Log Configuration</div>
                <div className="col-md-9 d-flex flex-wrap gap-3">
                  <FormRadio
                    name="logConfig"
                    value="All"
                    optionLabel="All"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="logConfig"
                    value="Manual"
                    optionLabel="Manual"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="logConfig"
                    value="Timer"
                    optionLabel="Timer"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                  <FormRadio
                    name="logConfig"
                    value="Task"
                    optionLabel="Task"
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <hr />

              {/* Start of Time Log Period Settings adjustments */}
              <div className="row mb-3">
                <div className="col-12 fw-bold">Time Log Period Settings</div>
              </div>

              {/* Log Past Time */}
              <div className="row mb-3 align-items-center">
                <div className="col-md-3 text-dark">Log past time</div>
                <div className="col-md-3 ms-3">
                  <FormSelect
                    name="allowPastTimeLogForm"
                    label={""}
                    options={[
                      { label: "Allow", value: "Allow" },
                      { label: "Don't Allow", value: "Don't Allow" },
                    ]}
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <div className="row mb-3 align-items-center">
                <div className="col-md-3 text-dark">Log future time</div>
                <div className="col-md-3 ms-3">
                  <FormSelect
                    name="allowFutureTimeLogForm"
                    label={""}
                    options={[
                      { label: "Allow", value: "Allow" },
                      { label: "Don't Allow", value: "Don't Allow" },
                    ]}
                    disabled={mode === "view"}
                    required={mode === "edit"}
                  />
                </div>
              </div>
              <hr />

              {/* Log Hour Limit Section */}
              <div className="row mb-3">
                <div className="col-md-3 fw-bold d-flex align-items-center">
                  Log Hour Limit
                  <Icon name="info" className="ms-2 text-warning" />
                </div>
                <div className="col-md-9 pb-3 d-flex align-items-center">
                  <FormCheckbox
                    name="enableHourLimit"
                    optionLabel="Enable Hour Limit"
                    onChange={(e) =>
                      setValue("enableHourLimit", e.target.checked)
                    }
                    disabled={mode === "view"}
                    // required={mode === "edit"}
                  />
                </div>
              </div>

              <div className="row mb-3 align-items-center">
                <div className="col-md-3 text-dark">Daily log hour limit</div>
                <div className="col-md-3 ms-3">
                  <div className="d-flex align-items-center gap-2">
                    <FormTimePicker
                      name="dailyHourLimitInSec"
                      label=""
                      disabled={!enableHourLimitWatch || mode === "view"}
                      className="w-60"
                    />
                    <span className="text-dark">hrs</span>
                  </div>
                </div>
              </div>
              <div className="row mb-3 align-items-center">
                <div className="col-md-3 text-dark">Weekly log hour limit</div>
                <div className="col-md-3 ms-3">
                  <div className="d-flex align-items-center gap-2">
                    <FormInput
                      name="weeklyHourLimitInSec"
                      label=""
                      type="number"
                      disabled={!enableHourLimitWatch || mode === "view"}
                      // className="w-50" // Adjusted width
                    />
                    <span className="text-dark">hrs</span>
                  </div>
                </div>
              </div>
              <hr />

              {/* Time Log Reminder Section */}
              <div className="row mb-3">
                <div className="col-md-3 fw-bold d-flex align-items-center">
                  Time Log Reminder
                  <Icon name="info" className="ms-2 text-warning" />
                </div>
                <div className="col-md-9 pb-3 d-flex align-items-center">
                  <FormCheckbox
                    name="enableReminder"
                    optionLabel="Enable Log Reminder"
                    onChange={(e) =>
                      setValue("enableReminder", e.target.checked)
                    }
                    disabled={mode === "view"}
                    // required={mode === "edit"}
                  />
                </div>
              </div>
              <div className="row mb-3 align-items-center">
                <div className="col-md-3 text-dark">
                  If log hour is less than
                </div>
                <div className="col-md-3 ms-3">
                  <div className="d-flex align-items-center gap-2">
                    <FormTimePicker
                      name="minLogHourInSec"
                      label=""
                      disabled={!enableReminderWatch || mode === "view"}
                      className="w-60"
                    />
                    <span className="text-dark">hrs / day</span>
                  </div>
                </div>
              </div>
              {/* Daily Reminder */}
              <div className="row mb-4 align-items-center">
                <div className="col-md-3 text-dark">Daily Reminder</div>
                <div className="col-md-6 ms-3 d-flex align-items-center gap-2">
                  <div style={{ minWidth: "183px" }}>
                    <FormInput
                      name="dailyReminderInSec"
                      label=""
                      type="number"
                      disabled={!enableReminderWatch || mode === "view"}
                    />
                  </div>
                  <span className="text-dark me-2">mins</span>
                  <div style={{ flexGrow: 1, minWidth: "220px" }}>
                    <FormSelect
                      name="dailyReminderTimeConfig"
                      label=""
                      options={[
                        {
                          label: "Before end of business hours",
                          value: "before_end",
                        },
                        // Add other options as needed
                      ]}
                      disabled={!enableReminderWatch || mode === "view"}
                      // NO className or style HERE
                    />
                  </div>
                  <FormCheckbox
                    name="notifyTimers"
                    optionLabel="Notify running timers."
                    disabled={!enableReminderWatch || mode === "view"}
                    className="flex-shrink-0"
                  />
                </div>
              </div>
              {/* Weekly Reminder */}
              <div className="row mb-3 align-items-center">
                <div className="col-md-3 text-dark">Weekly Reminder</div>
                <div className="col-md-6 ms-3 d-flex align-items-center gap-2">
                  <div className="w-24">
                    <FormInput
                      name="weeklyReminderInSec"
                      label=""
                      type="number"
                      disabled={!enableReminderWatch || mode === "view"}
                    />
                  </div>
                  <span className="text-dark me-3">mins</span>
                  <div style={{ minWidth: "220px" }}>
                    <FormSelect
                      name="weeklyReminderTimeConfig"
                      label=""
                      options={[
                        {
                          label: "Before end of business week",
                          value: "before_week_end",
                        },
                      ]}
                      disabled={!enableReminderWatch || mode === "view"}
                    />
                  </div>
                </div>
              </div>
              <hr />
            </form>
          </FormProvider>
        </Card>
      </Content>
    </>
  );
};

export default ProjectTimeLog;
